/**
 * Tanzanian Localization Utilities
 * Handles TZS currency, DD/MM/YYYY dates, 24hr time, and local data
 */

// Currency formatting (TZS - Tanzanian Shilling)
export const formatTZS = (amount: number): string => {
  return new Intl.NumberFormat("en-TZ", {
    style: "currency",
    currency: "TZS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Alternative format with comma separators
export const formatTZSCompact = (amount: number): string => {
  const formatted = new Intl.NumberFormat("en-TZ").format(amount);
  return `TZS ${formatted}`;
};

// Date formatting (DD/MM/YYYY)
export const formatDate = (date: string | Date): string => {
  const d = typeof date === "string" ? new Date(date) : date;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

// Date with time (DD/MM/YYYY HH:mm - 24hr format)
export const formatDateTime = (date: string | Date): string => {
  const d = typeof date === "string" ? new Date(date) : date;
  const datePart = formatDate(d);
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${datePart} ${hours}:${minutes}`;
};

// Relative time (e.g., "5 min ago", "2 hours ago")
export const formatRelativeTime = (date: string | Date): string => {
  const now = new Date();
  const d = typeof date === "string" ? new Date(date) : date;
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  return formatDate(d);
};

// Tanzanian Universities
export const TANZANIAN_UNIVERSITIES = [
  "University of Dar es Salaam (UDSM)",
  "Sokoine University of Agriculture (SUA)",
  "Muhimbili University of Health and Allied Sciences (MUHAS)",
  "Ardhi University (ARU)",
  "Mzumbe University (MU)",
  "University of Dodoma (UDOM)",
  "Nelson Mandela African Institution of Science and Technology (NM-AIST)",
  "Mwanza University (MWU)",
  "St. Augustine University of Tanzania (SAUT)",
  "Catholic University of Health and Allied Sciences (CUHAS)",
  "Hubert Kairuki Memorial University (HKMU)",
  "Institute of Finance Management (IFM)",
  "Institute of Social Work (ISW)",
  "Institute of Accountancy Arusha (IAA)",
  "College of Business Education (CBE)",
];

// Tanzanian Regions
export const TANZANIAN_REGIONS = [
  "Arusha",
  "Dar es Salaam",
  "Dodoma",
  "Geita",
  "Iringa",
  "Kagera",
  "Katavi",
  "Kigoma",
  "Kilimanjaro",
  "Lindi",
  "Manyara",
  "Mara",
  "Mbeya",
  "Morogoro",
  "Mtwara",
  "Mwanza",
  "Njombe",
  "Pemba North",
  "Pemba South",
  "Pwani",
  "Rukwa",
  "Ruvuma",
  "Shinyanga",
  "Simiyu",
  "Singida",
  "Songwe",
  "Tabora",
  "Tanga",
  "Zanzibar North",
  "Zanzibar South",
  "Zanzibar Urban/West",
];

// Major Cities
export const TANZANIAN_CITIES = [
  "Dar es Salaam",
  "Dodoma",
  "Mwanza",
  "Arusha",
  "Mbeya",
  "Tanga",
  "Morogoro",
  "Zanzibar City",
  "Kigoma",
  "Tabora",
  "Mtwara",
  "Iringa",
  "Singida",
  "Bukoba",
  "Musoma",
  "Shinyanga",
  "Njombe",
  "Lindi",
];

// Phone number validation (Tanzanian format)
export const validateTanzanianPhone = (phone: string): boolean => {
  // Remove spaces, dashes, and plus sign
  const cleaned = phone.replace(/[\s+-]/g, "");

  // Should be 9-12 digits starting with 255 or 07
  if (cleaned.startsWith("255")) {
    return cleaned.length === 12 && /^\d+$/.test(cleaned);
  }
  if (cleaned.startsWith("0")) {
    return cleaned.length === 10 && /^\d+$/.test(cleaned);
  }

  return false;
};

// Format phone number for display
export const formatTanzanianPhone = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, "");

  if (cleaned.startsWith("255") && cleaned.length === 12) {
    return `+${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 9)} ${cleaned.slice(9)}`;
  }
  if (cleaned.startsWith("0") && cleaned.length === 10) {
    return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
  }

  return phone;
};

// National ID validation (Tanzanian format: 1990-01-01-00123)
export const validateTanzanianNID = (nid: string): boolean => {
  const pattern = /^\d{4}-\d{2}-\d{2}-\d{5}$/;
  return pattern.test(nid);
};

// Days of the week in Swahili (optional for future use)
export const SWAHILI_DAYS = [
  "Jumapili", // Sunday
  "Jumatatu", // Monday
  "Jumanne", // Tuesday
  "Jumatano", // Wednesday
  "Alhamisi", // Thursday
  "Ijumaa", // Friday
  "Jumamosi", // Saturday
];

// Months in Swahili (optional for future use)
export const SWAHILI_MONTHS = [
  "Januari",
  "Februari",
  "Machi",
  "Aprili",
  "Mei",
  "Juni",
  "Julai",
  "Agosti",
  "Septemba",
  "Oktoba",
  "Novemba",
  "Desemba",
];

// Export all as a localization object
export const tzLocale = {
  formatTZS,
  formatTZSCompact,
  formatDate,
  formatDateTime,
  formatRelativeTime,
  validateTanzanianPhone,
  formatTanzanianPhone,
  validateTanzanianNID,
  universities: TANZANIAN_UNIVERSITIES,
  regions: TANZANIAN_REGIONS,
  cities: TANZANIAN_CITIES,
  swahiliDays: SWAHILI_DAYS,
  swahiliMonths: SWAHILI_MONTHS,
};

export default tzLocale;
